import React, { useState, useEffect } from 'react';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PayPalCheckout = ({ 
  customerInfo, 
  cart, 
  total, 
  deliveryMethod, 
  selectedShipping, 
  shippingCost,
  newsletterSignup,
  saveAddress,
  user,
  checkoutAsGuest,
  promoCode,
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState('CAD');
  const [{ options }] = usePayPalScriptReducer();
  
  // Get currency from PayPal SDK options
  useEffect(() => {
    if (options && options.currency) {
      setCurrency(options.currency);
      console.log('PayPal SDK currency:', options.currency);
    }
  }, [options]);

  // Create order in our database first, then create PayPal order
  const createOrder = async (data, actions) => {
    try {
      setLoading(true);
      
      console.log('Creating order with data:', {
        customerInfo,
        cartLength: cart.length,
        total,
        deliveryMethod,
        API
      });
      
      // Create pending order in our database
      const orderData = {
        customer_info: {
          ...customerInfo,
          newsletter_signup: newsletterSignup,
          save_address: saveAddress && user ? true : false
        },
        items: cart.map(item => ({
          product_id: item.product_id,
          product_name: item.product?.name || item.product_name,
          quantity: item.quantity,
          price: item.price,
          selected_options: item.selected_options || {},
          custom_text_inputs: item.custom_text_inputs || {},
          selected_addons: item.selected_addons || [],
          uploaded_files: item.uploaded_files || [],
          addon_total: item.addon_total || 0,
          option_total: item.option_total || 0
        })),
        payment_method: 'paypal',
        delivery_method: deliveryMethod,
        shipping_service: deliveryMethod === 'shipping' ? selectedShipping?.service_code : null,
        shipping_cost: deliveryMethod === 'shipping' ? shippingCost : 0,
        pickup_location: deliveryMethod === 'pickup' ? 'Cambridge, Ontario, Canada' : null,
        promo_code: promoCode,
        guest_checkout: !user || checkoutAsGuest,
        pending_payment: true
      };

      console.log('Posting order to:', `${API}/api/orders`);
      const response = await axios.post(`${API}/api/orders`, orderData);
      console.log('Order created successfully:', response.data);
      
      if (response.data && response.data.order) {
        const orderId = response.data.order.id;
        
        // Store order ID for later use
        sessionStorage.setItem('pending_paypal_order', orderId);
        
        console.log('Creating PayPal order with amount:', total.toFixed(2), 'currency:', currency);
        // Create PayPal order with dynamic currency from SDK
        return actions.order.create({
          purchase_units: [{
            amount: {
              value: total.toFixed(2),
              currency_code: currency // Use currency from PayPal SDK
            },
            description: `RG-3D Order #${response.data.order.order_number}`,
            custom_id: orderId  // Store our order ID
          }]
        });
      } else {
        throw new Error('Failed to create order - no order in response');
      }
    } catch (error) {
      console.error('Detailed error in createOrder:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error(error.response?.data?.detail || 'Failed to create order');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Handle successful payment
  const onApprove = async (data, actions) => {
    try {
      setLoading(true);
      
      // Capture the PayPal payment
      const details = await actions.order.capture();
      
      // Get our order ID from session storage
      const orderId = sessionStorage.getItem('pending_paypal_order');
      
      if (!orderId) {
        throw new Error('Order ID not found');
      }

      // Mark order as paid in our database
      const response = await axios.post(`${API}/api/orders/${orderId}/mark-paid`, {
        paypal_order_id: data.orderID,
        paypal_payer_id: data.payerID,
        payment_details: {
          transaction_id: details.purchase_units[0].payments.captures[0].id,
          payer_email: details.payer.email_address,
          payer_name: details.payer.name.given_name + ' ' + details.payer.name.surname,
          amount: details.purchase_units[0].payments.captures[0].amount.value,
          currency: details.purchase_units[0].payments.captures[0].amount.currency_code,
          status: details.status
        },
        payment_timestamp: new Date().toISOString()
      });

      if (response.data.success) {
        // Clear session storage
        sessionStorage.removeItem('pending_paypal_order');
        
        // Clear cart from localStorage
        localStorage.removeItem('cart');
        window.dispatchEvent(new Event('storage'));
        
        // Show success message
        toast.success('Payment successful! Your order has been confirmed.');
        
        // Call success callback
        if (onSuccess) {
          onSuccess({
            order_id: response.data.order_id,
            order_number: response.data.order_number,
            paypal_details: details
          });
        }
      } else {
        throw new Error('Failed to confirm payment');
      }
    } catch (error) {
      console.error('Payment confirmation error:', error);
      toast.error('Payment successful but order confirmation failed. Please contact support with your PayPal transaction ID.');
    } finally {
      setLoading(false);
    }
  };

  // Handle payment cancellation
  const onCancel = () => {
    toast.error('Payment cancelled');
    setLoading(false);
  };

  // Handle payment error
  const onError = (err) => {
    console.error('PayPal error:', err);
    toast.error('An error occurred with PayPal. Please try again.');
    setLoading(false);
  };

  return (
    <div className="w-full">
      {loading && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-700 text-center flex items-center justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            Processing...
          </p>
        </div>
      )}
      
      <PayPalButtons
        style={{ 
          layout: 'vertical',
          color: 'blue',
          shape: 'rect',
          label: 'pay'
        }}
        createOrder={createOrder}
        onApprove={onApprove}
        onCancel={onCancel}
        onError={onError}
        disabled={loading}
      />
      
      <p className="text-xs text-gray-500 mt-2 text-center">
        Your order will be created securely. Complete payment with PayPal.
      </p>
    </div>
  );
};

export default PayPalCheckout;
